import React, { useState, useRef } from 'react';
import { Trash2, Users, ChevronDown, ChevronUp, Upload, Camera } from 'lucide-react';
import { Card } from './ui/card';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import SearchableSelect from './ui/SearchableSelect';

const MARITAL_STATUS_OPTIONS = ['Unmarried / अविवाहित', 'Divorced / तलाकशुदा', 'Widow / विधवा', 'Widower / विधुर'];
const COMPLEXION_OPTIONS = ['Very Fair / बहुत गोरा', 'Fair / गोरा', 'Wheatish / गेहुआँ', 'Dark / साँवला'];
const BLOOD_GROUP_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];

const CandidateCard = ({ initialData, idx, expanded, onToggle, onDataChange, onFileChange, onDelete, canDelete, nukhOptions, candidateFiles }) => {
  const [data, setData] = useState(() => ({ ...initialData }));
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoRef = useRef(null);

  const handleChange = (field, value) => {
    const updated = { ...data, [field]: value };
    setData(updated);
    onDataChange(idx, updated);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      onFileChange(idx, 'photo', file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const renderField = (label, labelHi, name, opts = {}) => {
    const { type = 'text', required = false, placeholder = '', options } = opts;
    return (
      <div>
        <Label className="text-sm">
          {label} {labelHi && <span className="text-gray-400 text-xs">/ {labelHi}</span>}
          {required && <span className="text-destructive"> *</span>}
        </Label>
        {options ? (
          <select
            value={data[name] || ''}
            onChange={(e) => handleChange(name, e.target.value)}
            className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            required={required}
            data-testid={`candidate-${idx}-${name}`}
          >
            <option value="">Select / चुनें</option>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <input
            type={type}
            value={data[name] || ''}
            onChange={(e) => handleChange(name, e.target.value)}
            className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            required={required}
            placeholder={placeholder}
            data-testid={`candidate-${idx}-${name}`}
          />
        )}
      </div>
    );
  };

  return (
    <Card className="mb-6 overflow-hidden">
      <div
        className="flex items-center justify-between p-4 bg-primary/5 cursor-pointer"
        onClick={() => onToggle(idx)}
        data-testid={`candidate-${idx}-header`}
      >
        <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          Candidate {idx + 1} / उम्मीदवार {idx + 1}
          {data.name && <span className="text-sm font-normal text-gray-500">— {data.name}</span>}
        </h3>
        <div className="flex items-center gap-2">
          {canDelete && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(idx); }}
              className="text-red-500 hover:text-red-700 p-1"
              data-testid={`candidate-${idx}-delete`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </div>

      {expanded && (
        <div className="p-6 space-y-6">
          {/* Personal Info */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">Personal Information / व्यक्तिगत जानकारी</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {renderField("Full Name", "पूरा नाम", "name", { required: true })}
              {renderField("Father's Name", "पिता का नाम", "fatherName", { required: true })}
              {renderField("Mother's Name", "माता का नाम", "motherName")}
              <div>
                <Label className="text-sm">Nukh / नुख <span className="text-destructive">*</span></Label>
                <SearchableSelect
                  options={nukhOptions}
                  value={data.nukh}
                  onChange={(val) => handleChange('nukh', val)}
                  placeholder="Search nukh / नुख खोजें..."
                  allowCustom={false}
                  filterMode="startsWith"
                  className="mt-1"
                />
              </div>
              {/* Gender */}
              <div>
                <Label className="text-sm">
                  Gender <span className="text-gray-400 text-xs">/ लिंग</span>
                  <span className="text-destructive"> *</span>
                </Label>
                <select
                  value={data.gender || ''}
                  onChange={(e) => handleChange('gender', e.target.value)}
                  className="mt-1 flex h-10 w-full items-center rounded-md border border-input bg-background px-3 text-sm"
                  data-testid={`candidate-${idx}-gender`}
                >
                  <option value="">Select / चुनें</option>
                  <option value="Male / पुरुष">Male / पुरुष</option>
                  <option value="Female / महिला">Female / महिला</option>
                </select>
              </div>
              {renderField("Marital Status", "वैवाहिक स्थिति", "maritalStatus", { required: true, options: MARITAL_STATUS_OPTIONS })}
              {renderField("Date of Birth", "जन्म तिथि", "dateOfBirth", { type: 'date', required: true })}
              {renderField("Birth Time", "जन्म समय", "birthTime", { type: 'time' })}
              {renderField("Birth Place", "जन्म स्थान", "birthPlace")}
            </div>
          </div>

          {/* Family Details */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">Family Details / पारिवारिक विवरण</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {renderField("Unmarried Brothers", "अविवाहित भाई", "unmarriedBrothers", { type: 'number', placeholder: '0' })}
              {renderField("Married Brothers", "विवाहित भाई", "marriedBrothers", { type: 'number', placeholder: '0' })}
              {renderField("Unmarried Sisters", "अविवाहित बहन", "unmarriedSisters", { type: 'number', placeholder: '0' })}
              {renderField("Married Sisters", "विवाहित बहन", "marriedSisters", { type: 'number', placeholder: '0' })}
            </div>
          </div>

          {/* Maternal Details */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">Maternal Details / मामा पक्ष विवरण</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {renderField("Maternal Name", "मामा का नाम", "maternalName", { required: true })}
              <div>
                <Label className="text-sm">Nukh of Maternals <span className="text-gray-400 text-xs">/ मामा की नुख</span> <span className="text-destructive">*</span></Label>
                <SearchableSelect
                  options={nukhOptions}
                  value={data.nukhOfMaternals}
                  onChange={(val) => handleChange('nukhOfMaternals', val)}
                  placeholder="Search nukh / नुख खोजें..."
                  allowCustom={false}
                  filterMode="startsWith"
                  className="mt-1"
                />
              </div>
              {renderField("Place of Maternals", "मामा का स्थान", "placeOfMaternals", { required: true })}
            </div>
          </div>

          {/* Physical Details */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">Physical Details / शारीरिक विवरण</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {renderField("Height", "ऊँचाई", "height", { required: true, placeholder: "e.g., 5'6\"" })}
              {renderField("Weight (kg)", "वजन (किलो)", "weight", { type: 'number', required: true })}
              {renderField("Complexion", "रंग", "complexion", { options: COMPLEXION_OPTIONS })}
              {renderField("Blood Group", "रक्त समूह", "bloodGroup", { options: BLOOD_GROUP_OPTIONS })}
              {renderField("Manglik", "मांगलिक", "mapiOrManglik", { options: ['Yes / हाँ', 'No / नहीं', 'Partial / आंशिक'] })}
            </div>
          </div>

          {/* Education & Career */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">Education & Career / शिक्षा एवं करियर</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {renderField("Education", "शिक्षा", "education", { required: true, placeholder: "e.g., B.Tech, MBA" })}
              {renderField("Occupation", "व्यवसाय", "occupation", { required: true, placeholder: "e.g., Software Engineer" })}
              {renderField("Working At", "कार्यस्थल", "workingAt", { required: true, placeholder: "Company / Organization" })}
              {renderField("Annual Income", "वार्षिक आय", "annualIncome", { placeholder: "e.g., 5-10 LPA" })}
            </div>
          </div>

          {/* Contact Details */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">Contact Details / संपर्क विवरण</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {renderField("Phone (10 digits)", "फ़ोन (10 अंक)", "phone", { required: true, placeholder: "9876543210" })}
              {renderField("WhatsApp (10 digits)", "व्हाट्सएप (10 अंक)", "whatsapp", { required: true, placeholder: "9876543210" })}
              {renderField("Email", "ईमेल", "email", { type: 'email', required: true, placeholder: "candidate@email.com" })}
            </div>
          </div>

          {/* Current Address */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">Current Address / वर्तमान पता</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                {renderField("Address", "पता", "currentAddress", { required: true })}
              </div>
              {renderField("Place", "स्थान", "currentPlace", { required: true })}
              {renderField("State", "राज्य", "currentState", { required: true })}
              {renderField("Country", "देश", "currentCountry", { required: true })}
            </div>
          </div>

          {/* Native Place */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">Native Place / मूल निवास</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderField("Native Place", "मूल स्थान", "nativePlace", { required: true })}
              {renderField("Native State", "मूल राज्य", "nativeState", { required: true })}
            </div>
          </div>

          {/* About & Preferences */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">About & Preferences / परिचय एवं प्राथमिकता</h4>
            <div className="space-y-4">
              <div>
                <Label className="text-sm">Hobbies & Interests / शौक एवं रुचियाँ</Label>
                <Textarea value={data.hobbies || ''} onChange={(e) => handleChange('hobbies', e.target.value)} rows={2} className="mt-1" data-testid={`candidate-${idx}-hobbies`} />
              </div>
              <div>
                <Label className="text-sm">About Candidate / उम्मीदवार के बारे में</Label>
                <Textarea value={data.aboutCandidate || ''} onChange={(e) => handleChange('aboutCandidate', e.target.value)} rows={2} className="mt-1" data-testid={`candidate-${idx}-aboutCandidate`} />
              </div>
              <div>
                <Label className="text-sm">Partner Preference / जीवन साथी की प्राथमिकता</Label>
                <Textarea value={data.partnerPreference || ''} onChange={(e) => handleChange('partnerPreference', e.target.value)} rows={2} className="mt-1" data-testid={`candidate-${idx}-partnerPreference`} />
              </div>
            </div>
          </div>

          {/* Photo Upload */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">Photo / फोटो <span className="text-destructive">*</span></h4>
            <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors" data-testid={`candidate-${idx}-photo-upload`}>
              {candidateFiles?.photo ? (
                <><Camera className="w-4 h-4 text-green-600" /><span className="text-sm text-green-700 font-medium">{candidateFiles.photo.name}</span></>
              ) : (
                <><Upload className="w-4 h-4 text-gray-400" /><span className="text-sm text-gray-500">Choose photo (JPG, PNG, max 5MB)</span></>
              )}
              <input ref={photoRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePhotoChange} />
            </label>
            {photoPreview && (
              <div className="mt-2 flex items-center gap-2">
                <img src={photoPreview} alt="Preview" className="w-16 h-16 object-cover rounded border" />
                <button type="button" onClick={() => { onFileChange(idx, 'photo', null); setPhotoPreview(null); }} className="text-xs text-red-500 hover:underline">Remove</button>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

export default CandidateCard;
